import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/toast';
import { useUpdateMedia } from '@/hooks/use-media';
import { describeError } from '@/lib/api-client';
import type { MediaItem } from '@/types/api';

/**
 * An administrator's own title and description for a file. Saved text is theirs: the describer
 * leaves it alone from then on, unless someone explicitly regenerates it.
 */
export function MediaEditDialog({
  media,
  open,
  onOpenChange,
}: {
  media: MediaItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const updateMedia = useUpdateMedia();
  const { toast } = useToast();
  const noun = media.kind === 'video' ? 'video' : 'image';

  const [title, setTitle] = useState(media.title ?? '');
  const [description, setDescription] = useState(media.description ?? '');

  // Reset the form whenever the dialog is (re)opened for a file.
  useEffect(() => {
    if (!open) return;
    setTitle(media.title ?? '');
    setDescription(media.description ?? '');
  }, [open, media]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updateMedia.mutate(
      {
        mediaId: media.mediaId,
        body: { title: title.trim(), description: description.trim() },
      },
      {
        onSuccess: () => {
          toast({ title: `${noun === 'video' ? 'Video' : 'Image'} updated`, variant: 'success' });
          onOpenChange(false);
        },
        onError: (error) =>
          toast({
            title: `Could not update the ${noun}`,
            description: describeError(error),
            variant: 'error',
          }),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Edit {noun}</DialogTitle>
          <DialogDescription>
            Replaces the generated title and description. Yours are kept until someone regenerates
            them.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="edit-media-title">Title</Label>
            <Input
              id="edit-media-title"
              value={title}
              required
              // Keep in sync with updateMediaSchema in @slc/validation.
              maxLength={200}
              onChange={(event) => setTitle(event.target.value)}
              placeholder={media.name}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="edit-media-description">Description</Label>
            <Textarea
              id="edit-media-description"
              value={description}
              maxLength={1000}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={`What the ${noun} shows or is about.`}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={updateMedia.isPending} disabled={!title.trim()}>
              Save changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
